// Fish species data, spawning, and swim behavior.
// Extracted from legacy/tsuri.html — logic and constants are unchanged.

import * as THREE from 'three';
import { worldToHex, PROP_SCALE } from '@world/terrain';
import { toonMat } from '@world/toon';
import { waterInfoAt, habitatAt, type Habitat } from '@world/water';

const IS_TOUCH = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

export interface FishType {
  name: string;
  color: number;
  size: number;
  speed: number;
  depth: number;
}

export const FISH_TYPES: readonly FishType[] = [
  { name: 'Perch',   color: 0x6f9c3f, size: 0.15, speed: 0.4,  depth: 0.2 },
  { name: 'Trout',   color: 0x9c8f6a, size: 0.17, speed: 0.5,  depth: 0.28 },
  { name: 'Bass',    color: 0x5c7a4a, size: 0.19, speed: 0.42, depth: 0.32 },
  { name: 'Carp',    color: 0xb08d4f, size: 0.21, speed: 0.28, depth: 0.4 },
  { name: 'Salmon',  color: 0xd97a6e, size: 0.2,  speed: 0.55, depth: 0.3 },
  { name: 'Tilapia', color: 0x8f9aa8, size: 0.16, speed: 0.35, depth: 0.26 },
  { name: 'Pike',    color: 0x5f7d68, size: 0.24, speed: 0.6,  depth: 0.38 },
  { name: 'Catfish', color: 0x7a838d, size: 0.27, speed: 0.24, depth: 0.5 },
];

// Species favor their habitat: get a 3.5x spawn weight in home waters.
export const HABITAT_SPECIES: Record<Habitat, string[]> = {
  deep: ['Catfish', 'Pike', 'Carp'],
  mangrove: ['Bass', 'Perch', 'Tilapia'],
  coral: ['Tilapia', 'Salmon', 'Trout'],
  shellfish: ['Carp', 'Perch', 'Tilapia'],
  open: ['Trout', 'Salmon', 'Perch'],
};

export interface FishInstance {
  mesh: THREE.Mesh;
  typeIndex: number;
  heading: number;
  bobPhase: number;
  homeX: number;
  homeZ: number;
}

export const fishGroup = new THREE.Group();
const fishList: FishInstance[] = [];
const MAX_FISH = IS_TOUCH ? 10 : 16;
const TERRITORY_R = 1.7;

function buildFishGeometry(): THREE.BufferGeometry {
  const nose = [1.0, 0, 0];
  const tailBase = [-0.62, 0, 0];
  const top = [0.05, 0.3, 0];
  const bottom = [0.05, -0.26, 0];
  const left = [0.05, 0, 0.2];
  const right = [0.05, 0, -0.2];
  const tailTop = [-1.05, 0.26, 0];
  const tailBot = [-1.05, -0.26, 0];
  const finA = [0.15, 0.28, 0];
  const finB = [-0.2, 0.55, 0];
  const finC = [-0.32, 0.26, 0];
  const tris = [
    [nose, top, left], [nose, left, bottom], [nose, bottom, right], [nose, right, top],
    [tailBase, left, top], [tailBase, bottom, left], [tailBase, right, bottom], [tailBase, top, right],
    [tailBase, tailTop, tailBot],
    [finA, finB, finC],
  ];
  const positions: number[] = [];
  tris.forEach((t) => t.forEach((p) => positions.push(p[0], p[1], p[2])));
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

export const fishGeo = buildFishGeometry();
export const fishMats = FISH_TYPES.map((t) => {
  const m = toonMat(t.color);
  m.side = THREE.DoubleSide; // the fish body is a flat shell, seen from both sides
  return m;
});

export function fishMatFor(name: string): THREE.MeshToonMaterial {
  const i = FISH_TYPES.findIndex((t) => t.name === name);
  return fishMats[Math.max(0, i)];
}

export function trySpawnFish(centerX: number, centerZ: number): void {
  if (fishList.length >= MAX_FISH) return;
  for (let attempt = 0; attempt < 6; attempt++) {
    const ang = Math.random() * Math.PI * 2;
    const dist = 3 + Math.random() * 9;
    const x = centerX + Math.cos(ang) * dist;
    const z = centerZ + Math.sin(ang) * dist;
    const w = waterInfoAt(x, z);
    if (!w) continue;
    const hx2 = worldToHex(x, z);
    const fav = HABITAT_SPECIES[habitatAt(hx2.q, hx2.r) || 'open'] || [];
    let total = 0;
    const weights = FISH_TYPES.map((ft) => {
      const wt = fav.includes(ft.name) ? 3.5 : 1;
      total += wt;
      return wt;
    });
    let roll = Math.random() * total;
    let typeIndex = 0;
    for (let i = 0; i < weights.length; i++) {
      roll -= weights[i];
      if (roll <= 0) { typeIndex = i; break; }
    }
    const t = FISH_TYPES[typeIndex];
    // Both offsets scale with the fish itself — the water column is much
    // shallower now, so a fixed depth would push them through the bed.
    const y = Math.max(w.bedTopY + 0.12 * PROP_SCALE, w.surfaceY - t.depth * PROP_SCALE);
    const mesh = new THREE.Mesh(fishGeo, fishMats[typeIndex]);
    mesh.scale.setScalar(t.size * 1.15 * PROP_SCALE);
    mesh.position.set(x, y, z);
    const fish: FishInstance = { mesh, typeIndex, heading: Math.random() * Math.PI * 2, bobPhase: Math.random() * 10, homeX: x, homeZ: z };
    mesh.userData.fish = fish;
    fishGroup.add(mesh);
    fishList.push(fish);
    return;
  }
}

function angleDiff(a: number, b: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

export function updateFish(dt: number, now: number, centerX: number, centerZ: number): void {
  for (let i = fishList.length - 1; i >= 0; i--) {
    const f = fishList[i];
    const t = FISH_TYPES[f.typeIndex];
    const ddx = f.mesh.position.x - centerX, ddz = f.mesh.position.z - centerZ;
    if (ddx * ddx + ddz * ddz > 26 * 26) {
      fishGroup.remove(f.mesh);
      fishList.splice(i, 1);
      continue;
    }
    f.heading += (Math.random() - 0.5) * dt * 1.6;
    const hx = f.homeX - f.mesh.position.x, hz = f.homeZ - f.mesh.position.z;
    if (Math.hypot(hx, hz) > TERRITORY_R) {
      f.heading += angleDiff(f.heading, Math.atan2(hz, hx)) * Math.min(1, dt * 3.5);
    }
    const nx = f.mesh.position.x + Math.cos(f.heading) * t.speed * dt;
    const nz = f.mesh.position.z + Math.sin(f.heading) * t.speed * dt;
    const w = waterInfoAt(nx, nz);
    if (!w) {
      f.heading += Math.PI * (0.7 + Math.random() * 0.6);
    } else {
      f.mesh.position.x = nx;
      f.mesh.position.z = nz;
      const targetY = Math.max(w.bedTopY + 0.12 * PROP_SCALE, w.surfaceY - t.depth * PROP_SCALE);
      f.mesh.position.y += (targetY - f.mesh.position.y) * Math.min(1, dt * 2);
    }
    f.mesh.position.y += Math.sin(now * 0.002 + f.bobPhase) * 0.0006;
    f.mesh.rotation.y = -f.heading + Math.sin(now * 0.009 + f.bobPhase) * 0.15;
  }
}

// Returns the first fish within radius of (x, z) — used by the casting
// mechanic to decide whether a bite occurs. Callers own removal (e.g. on
// a successful catch) via removeFish.
export function findFishWithin(x: number, z: number, radius: number): FishInstance | undefined {
  for (const f of fishList) {
    const d = Math.hypot(f.mesh.position.x - x, f.mesh.position.z - z);
    if (d < radius) return f;
  }
  return undefined;
}

export function removeFish(fish: FishInstance): void {
  const idx = fishList.indexOf(fish);
  if (idx >= 0) fishList.splice(idx, 1);
  fishGroup.remove(fish.mesh);
}

// Removes and returns up to `limit` fish within radius of (x, z) — used
// by the net-fishing mechanic, which (unlike a rod cast) can haul in a
// whole school at once.
export function collectFishWithin(x: number, z: number, radius: number, limit: number): FishInstance[] {
  const caught: FishInstance[] = [];
  for (let i = fishList.length - 1; i >= 0; i--) {
    if (caught.length >= limit) break;
    const f = fishList[i];
    const d = Math.hypot(f.mesh.position.x - x, f.mesh.position.z - z);
    if (d < radius) {
      caught.push(f);
      fishList.splice(i, 1);
      fishGroup.remove(f.mesh);
    }
  }
  return caught;
}
